import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, HandHeart, Lock, EyeOff, Send, Shield } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

const CATEGORIES = ["All", "General", "Healing", "Family", "Provision", "Salvation", "Nation"];

export default function Prayer() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [prayedIds, setPrayedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("prayed_ids") || "[]")); }
    catch { return new Set(); }
  });

  const empty = {
    name: user?.name || "",
    email: user?.email || "",
    message: "",
    category: "General",
    is_anonymous: false,
    is_private: false,
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    const url = category === "All" ? "/prayer" : `/prayer?category=${encodeURIComponent(category)}`;
    api.get(url).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [category]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 8) {
      toast.error("Please share a bit more about your request.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/prayer", {
        name: form.is_anonymous ? "Anonymous" : form.name.trim(),
        email: form.email || undefined,
        message: form.message.trim(),
        category: form.category,
        is_anonymous: form.is_anonymous,
        is_private: form.is_private,
      });
      toast.success(form.is_private ? "Received. Our team will be praying." : "Your request is on the wall — the family is praying.");
      setForm({ ...empty, name: form.name, email: form.email });
      if (!form.is_private && form.category === category) {
        // Add optimistically for public requests in the current view
        setItems((prev) => [data.prayer, ...prev]);
      } else if (!form.is_private && category === "All") {
        setItems((prev) => [data.prayer, ...prev]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const pray = async (id) => {
    if (prayedIds.has(id)) return;
    try {
      const { data } = await api.post(`/prayer/${id}/pray`);
      setItems((prev) => prev.map((p) => p.id === id ? { ...p, pray_count: data.pray_count } : p));
      const next = new Set(prayedIds);
      next.add(id);
      setPrayedIds(next);
      localStorage.setItem("prayed_ids", JSON.stringify([...next]));
      if (!data.already_prayed) toast.success("Prayer sent. The Lord hears.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const totalPrayers = useMemo(() => items.reduce((n, p) => n + (p.pray_count || 0), 0), [items]);

  return (
    <div data-testid="prayer-page">
      <PageHero
        overline="Prayer Wall"
        title={<>A family that <span className="font-accent text-gold-gradient italic">prays together.</span></>}
        subtitle="Share your request. Stand in agreement with others. Every request is bathed in prayer — publicly on the wall or privately with our intercession team."
      />

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-24">
        <div className="grid gap-12 lg:grid-cols-[420px_1fr]">
          {/* Submit form */}
          <Reveal>
            <div className="lg:sticky lg:top-28 border border-gold-500/25 bg-ink-paper/40 p-7" data-testid="prayer-form-card">
              <div className="flex items-center gap-3 mb-1">
                <HandHeart size={20} className="text-gold-500" />
                <Overline>Share a request</Overline>
              </div>
              <p className="text-sm text-white/60 mt-3 mb-6">Your request will be lifted before the Lord. Choose privacy that fits your heart.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="prayer-form">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    required={!form.is_anonymous}
                    disabled={form.is_anonymous}
                    placeholder={form.is_anonymous ? "Anonymous" : "Your name"}
                    value={form.is_anonymous ? "" : form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="prayer-name"
                    className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
                  />
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="prayer-category"
                    className="bg-ink border border-gold-500/25 text-white/90 px-3 py-2 text-sm outline-none focus:border-gold-400"
                  >
                    {CATEGORIES.filter((c) => c !== "All").map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <Input
                  type="email"
                  placeholder="Email (optional — for a private reply)"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="prayer-email"
                  className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
                />
                <Textarea
                  required
                  rows={5}
                  minLength={8}
                  maxLength={1500}
                  placeholder="Share your prayer request…"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="prayer-message"
                  className="bg-ink border-gold-500/25 text-white placeholder:text-white/40 resize-none"
                />

                <div className="pt-2 space-y-3">
                  <label className="flex items-center justify-between gap-3 cursor-pointer" data-testid="toggle-anonymous">
                    <span className="flex items-center gap-2 text-sm text-white/75">
                      <EyeOff size={15} className="text-gold-400" /> Post anonymously
                    </span>
                    <Switch
                      checked={form.is_anonymous}
                      onCheckedChange={(v) => setForm({ ...form, is_anonymous: v })}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-3 cursor-pointer" data-testid="toggle-private">
                    <span className="flex items-center gap-2 text-sm text-white/75">
                      <Lock size={15} className="text-gold-400" /> Private (intercession team only)
                    </span>
                    <Switch
                      checked={form.is_private}
                      onCheckedChange={(v) => setForm({ ...form, is_private: v })}
                    />
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="prayer-submit"
                  className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold"
                >
                  <Send size={15} className="mr-2" />
                  {submitting ? "Sending…" : "Submit prayer request"}
                </Button>

                <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 pt-2 flex items-center gap-1.5">
                  <Shield size={12} className="text-gold-500/70" /> Requests are moderated with love.
                </p>
              </form>
            </div>
          </Reveal>

          {/* Feed */}
          <div>
            <Reveal className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <div>
                <Overline>The wall</Overline>
                <p className="mt-2 text-sm text-white/60" data-testid="prayer-stats">
                  {items.length} {items.length === 1 ? "request" : "requests"} · {totalPrayers} {totalPrayers === 1 ? "prayer" : "prayers"} lifted
                </p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="prayer-categories">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    data-testid={`cat-${c.toLowerCase()}`}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                      category === c ? "border-gold-400 text-gold-300 bg-gold-500/10" : "border-white/15 text-white/60 hover:text-white"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </Reveal>

            {loading ? (
              <p className="text-white/50 py-14 text-center border border-gold-500/15" data-testid="prayer-loading">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-white/50 py-14 text-center border border-gold-500/15" data-testid="prayer-empty">
                No requests here yet. Be the first to share.
              </p>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2" data-testid="prayer-grid">
                <AnimatePresence>
                  {items.map((p) => {
                    const iPrayed = prayedIds.has(p.id);
                    return (
                      <motion.article
                        key={p.id}
                        layout
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="flex flex-col justify-between border border-gold-500/20 bg-ink-paper/40 p-6"
                        data-testid={`prayer-card-${p.id}`}
                      >
                        <div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-gold-400 border border-gold-500/30 px-2.5 py-1">
                              {p.category}
                            </span>
                            <span className="text-xs text-white/40">{timeAgo(p.created_at)}</span>
                          </div>
                          <p className="mt-5 text-white/85 leading-relaxed text-[15px] whitespace-pre-wrap">
                            {p.message}
                          </p>
                        </div>
                        <div className="mt-6 pt-5 border-t border-gold-500/15 flex items-center justify-between gap-3">
                          <p className="text-sm text-white/60">
                            <span className="text-gold-300">— {p.name}</span>
                          </p>
                          <button
                            onClick={() => pray(p.id)}
                            disabled={iPrayed}
                            data-testid={`pray-btn-${p.id}`}
                            className={`inline-flex items-center gap-2 border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                              iPrayed
                                ? "border-gold-500/50 bg-gold-500/10 text-gold-300 cursor-default"
                                : "border-gold-500/30 text-white hover:bg-gold-500 hover:text-ink hover:border-gold-500"
                            }`}
                          >
                            <Heart size={13} fill={iPrayed ? "currentColor" : "none"} />
                            <span>{iPrayed ? "Praying" : "I'm praying"}</span>
                            <span className="text-gold-400">· {p.pray_count || 0}</span>
                          </button>
                        </div>
                      </motion.article>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
